import {debounce} from 'es-toolkit';
import Queue from 'promise-queue';

import {bypassMobileHeaderScrollEventAction} from 'app/components/mobileHeader/store/actions';
import {gemStore} from 'app/gemStore';
import {getCidSelectorFromHash, getSelectorFromHash} from 'app/utils/location/getSelectorFromHash';
import {scrollToTop} from 'app/utils/scrollToTop';

import {Nil, Nullable} from '../interface';

import {delegateEvent, getPosition} from './jqueryNative';

const promiseQueue = new Queue(1);

export function initSmoothPageScroll() {
    delegateEvent('click', '[data-scroll-anchor]', onClickScroll());
}

function onClickScroll() {
    return function (evt: MouseEvent) {
        if (canScroll(this)) {
            scrollToTarget(evt, this);
        } else if (this.dataset.scrollAnchor === 'top') {
            scrollToTop();
        }
    };
}

export function canScroll(link: HTMLAnchorElement): boolean {
    try {
        const url = link.href ? new URL(link.href) : undefined;
        return link.matches('a[href*="#"]:not([href="#"])') && window.location.pathname === url?.pathname && window.location.hostname === url.hostname;
    } catch (err) {
        console.warn('Not valid URL!', err);
        return false;
    }
}

function scrollToTarget(evt: MouseEvent, element: HTMLAnchorElement) {
    let targetToScrollTo = getTargetToScrollTo(element.hash);
    if (targetToScrollTo) {
        evt.preventDefault();
    } else {
        targetToScrollTo = getTargetToScrollToByCid(element.hash);
    }

    if (!targetToScrollTo || targetToScrollTo.closest('[aria-hidden="true"]')) {
        return;
    }

    const topOffset = getPosition(targetToScrollTo).top;
    const componentsOffset = getOffset(targetToScrollTo);
    const pageHeight = document.body.scrollHeight;

    disableMobileHeaderScrollEvent();

    smoothScroll(topOffset - componentsOffset).then(() => {
        if (!targetToScrollTo) {
            return;
        }

        if (process.env.NODE_ENV === 'test') {
            recalculateScrollPosition(targetToScrollTo, pageHeight, componentsOffset, element);
        } else {
            setTimeout(() => {
                recalculateScrollPosition(targetToScrollTo, pageHeight, componentsOffset, element);
            }, 500);
        }
    });
}

/**
 * Offset must be recalculated:
 * 1. lazy-loading could be on page
 * 2. while scrolling, some sticky components can hide
 * @param targetToScrollTo
 * @param pageHeight
 * @param componentsOffset
 * @param element
 */
function recalculateScrollPosition(targetToScrollTo: HTMLElement, pageHeight: number, componentsOffset: number, element: HTMLAnchorElement) {
    const newComponentsOffset = getOffset(targetToScrollTo);
    if (pageHeight !== document.body.scrollHeight || newComponentsOffset !== componentsOffset) {
        element.click();
    } else {
        enableMobileHeaderScrollEvent();
        focusTarget(targetToScrollTo);
    }
}

/**
 * Find the target by element or toggle ID
 * @param hash
 */
function getTargetToScrollTo(hash: string): Nil<HTMLElement> {
    let targetToScrollTo: Nil<HTMLElement> = null;
    const selector = getSelectorFromHash(hash);
    if (selector) {
        targetToScrollTo = document.querySelector<HTMLElement>(selector);
    }

    if (!targetToScrollTo) {
        const path = hash.slice(1);
        targetToScrollTo = document.querySelector<HTMLElement>(`[name="${path}"]`);
    }

    return targetToScrollTo;
}

/**
 * Find component by cId
 * @param hash
 */
function getTargetToScrollToByCid(hash: string): Nullable<HTMLElement> {
    let targetToScrollTo: Nullable<HTMLElement> = null;
    const selector = getCidSelectorFromHash(hash);
    if (selector) {
        targetToScrollTo = document.querySelector<HTMLElement>(selector);
        if (targetToScrollTo) {
            targetToScrollTo = targetToScrollTo.previousElementSibling as Nullable<HTMLElement>;
        }
    }

    return targetToScrollTo;
}

export function scrollWithOffset(target: HTMLElement, bias = 0) {
    disableMobileHeaderScrollEvent();
    return promiseQueue.add(() => scrollWithOffsetRecursive(target, bias));
}

async function scrollWithOffsetRecursive(target: HTMLElement, bias: number) {
    const topBias = getOffset(target) - bias;
    const pageHeight = document.body.scrollHeight;
    await scroll2element(target, topBias);
    const newBias = getOffset(target) - bias;
    if (pageHeight !== document.body.scrollHeight || newBias !== topBias) {
        return scrollWithOffsetRecursive(target, bias);
    }

    enableMobileHeaderScrollEvent();
    focusTarget(target);
}

function scroll2element(target?: HTMLElement, bias = 0): Promise<void> {
    const topBias = bias || 0;
    let topOffset = 0;
    if (target) {
        topOffset = getPosition(target).top;
    }

    return smoothScroll(topOffset - topBias);
}

function getOffset(target: HTMLElement) {
    if (target.classList.contains('columns')) {
        return 0;
    }

    return gemStore
        .getState()
        .stickyStack.filter((item) => item.placement === 'TOP' && item.active && item.cId !== target.dataset.cid)
        .reduce((top, item) => {
            return top + item.height;
        }, 0);
}

function disableMobileHeaderScrollEvent() {
    bypassMobileHeaderScrollEventAction(true);
}

function enableMobileHeaderScrollEvent() {
    bypassMobileHeaderScrollEventAction(false);
}

function focusTarget(target: HTMLElement): void {
    target.focus({preventScroll: true});
    if (document.activeElement !== target) {
        target.setAttribute('tabIndex', '-1');
        target.focus({preventScroll: true});
    }
}

function smoothScroll(top: number, left = 0): Promise<void> {
    return new Promise((resolve) => {
        const pageAtEndButScrollDirectionIsDown = document.body.offsetHeight - window.innerHeight <= window.scrollY && top > window.scrollY;
        const roundedTop = Math.ceil(top);
        const pageAtSamePositionAsTop = roundedTop === window.scrollY;
        if (pageAtEndButScrollDirectionIsDown || pageAtSamePositionAsTop) {
            // not possible to scroll anymore, just resolve the promise
            resolve();
        } else {
            const onScrollEnd = debounce(() => {
                document.removeEventListener('scroll', onScrollEnd);
                resolve();
            }, 50);

            document.addEventListener('scroll', onScrollEnd);
            window.scroll({
                left,
                top: roundedTop,
                behavior: 'smooth',
            });
        }
    });
}
